import { computed, defineStore, storeFactory, useSupabase } from "#imports";
import type { QueryData } from "@supabase/supabase-js";
import { type GetterType } from "./utils";

export type Creator = GetterType<ReturnType<typeof useCreators>["getById"]>;

export const useCreators = defineStore("creators", () => {
  const supabase = useSupabase();

  const selectCreators = (withCount: boolean = false) =>
    supabase
      .from("profiles")
      .select(
        `*, profile_emails (email), maps!maps_user_id_fkey(id)`,
        withCount ? { count: "exact" } : undefined,
      )
      .eq("is_creator", true)
      .order("created_at", { ascending: false })
      .throwOnError();

  type RawCreator = QueryData<ReturnType<typeof selectCreators>>[number];
  const mapCreator = (creator: RawCreator) => ({
    ...creator,
    email: creator.profile_emails[0]?.email ?? undefined,
    mapIds: creator.maps.map((map) => map.id),
    maps: undefined,
    profile_emails: undefined,
  });

  const {
    dataById,
    fetchFactory,
    fetchOneByKeyFactory,
    fetchById,
    fetchByIds,
    getById,
    getByIds,
  } = storeFactory<RawCreator, ReturnType<typeof mapCreator>>(
    "creators",
    async (payload) => await selectCreators().in("id", payload),
    mapCreator,
  );

  const {
    metadata: metadataRecentCreators,
    fetch: fetchRecentCreators,
    result: recentCreators,
  } = fetchFactory<{ limit: number }>(
    async (payload) =>
      await selectCreators()
        .eq("is_published", true)
        .order("created_at", { ascending: false })
        .limit(payload.limit),
  );

  const {
    metadata: metadataCreatorsWithMaps,
    fetch: fetchCreatorsWithMaps,
    result: creatorsWithMaps,
  } = fetchFactory(
    async () =>
      await selectCreators()
        .eq("is_published", true)
        .eq("maps.is_published", true),
  );

  const {
    metadata: metadataByHandle,
    fetch: fetchCreatorByHandle,
    result: creatorByHandle,
    keyToId: handleToId,
  } = fetchOneByKeyFactory(
    "mappr_handle",
    async (mappr_handle) =>
      await selectCreators().eq("mappr_handle", mappr_handle).single(),
  );

  const getCreatorsByHandles = (mappr_handles: string[]) =>
    computed(() => {
      const ids = mappr_handles
        .map((handle) => handleToId.value[handle])
        .filter((id) => id !== undefined);
      return getByIds(ids).value;
    });

  const getCreatorByHandle = (mappr_handle: string) =>
    computed(() => {
      const id = handleToId.value[mappr_handle];
      return getById(id).value;
    });

  const {
    metadata: metadataByHandles,
    fetch: fetchCreatorByHandles,
    result: creatorByHandles,
  } = fetchFactory<{ handles: string[] }>(
    async (payload) =>
      await selectCreators().in("mappr_handle", payload.handles),
  );

  const {
    metadata: metadataFeaturedCreators,
    fetch: fetchFeaturedCreators,
    result: featuredCreators,
  } = fetchFactory<{ limit: number }>(
    async (payload) =>
      await selectCreators()
        .eq("is_featured", true)
        .eq("is_published", true)
        .order("created_at", { ascending: false })
        .limit(payload.limit),
  );

  const {
    metadata: metadataSearch,
    fetch: search,
    result: searchResults,
    totalRows: searchTotalRows,
  } = fetchFactory<{
    search: string;
    limit?: number;
    offset?: number;
    withTotal?: boolean;
  }>(async (payload) => {
    const search = payload.search.toLowerCase();
    if (search.length === 0) return { data: [], count: 0 };
    const searchFields =
      ["mappr_handle", "display_name", "first_name", "last_name"].join(
        `.ilike.${search}%,`,
      ) + `.ilike.${search}%`;

    return await selectCreators(payload.withTotal)
      .or(`${searchFields},fts.wfts.${search}`)
      .range(
        payload.offset ?? 0,
        (payload.offset ?? 0) + (payload.limit ?? 20) - 1,
      );
  });

  const {
    metadata: metadataAll,
    fetch: fetchAll,
    result: allCreators,
    totalRows: allTotalRows,
  } = fetchFactory<{
    limit: number;
    offset: number;
    sortBy: string;
    sortDirection?: "asc" | "desc" | null;
  }>(async (payload) => {
    return await selectCreators(true)
      .order(payload.sortBy, { ascending: payload.sortDirection !== "desc" })
      .range(payload.offset, payload.offset + payload.limit - 1);
  });

  return {
    dataById, // do not use, only for nuxt SSR cache transfer

    fetchById,
    getById,

    fetchByIds,
    getByIds,

    metadataRecentCreators,
    fetchRecentCreators,
    recentCreators,

    metadataByHandle,
    creatorByHandle,
    fetchCreatorByHandle,
    getCreatorsByHandles,
    getCreatorByHandle,

    metadataByHandles,
    fetchCreatorByHandles,
    creatorByHandles,

    metadataFeaturedCreators,
    featuredCreators,
    fetchFeaturedCreators,

    metadataSearch,
    search,
    searchResults,
    searchTotalRows,

    metadataAll,
    fetchAll,
    allCreators,
    allTotalRows,
    metadataCreatorsWithMaps,
    fetchCreatorsWithMaps,
    creatorsWithMaps,
  };
});
